import * as React from "react";
import { FormRow } from "../../components/FormRow";
import { Input } from "../../components/Input";
import type { Field } from "../types";

interface Props {
  field: Field;
  value: number | null;
  placeholder?: string;
  /** Called with `null` when the input is cleared, so the parent can remove
   *  the field from the draft (translating to a delete in the PUT payload).
   *  This lets the env-var fallback re-surface as a placeholder on the next
   *  GET; storing 0 instead would shadow any env value. */
  onChange: (v: number | null) => void;
  disabled?: boolean;
}

export function IntField({
  field,
  value,
  placeholder,
  onChange,
  disabled,
}: Props) {
  const clamp = (n: number): number => {
    if (field.min !== undefined && n < field.min) return field.min;
    if (field.max !== undefined && n > field.max) return field.max;
    return n;
  };
  const hasValue =
    value !== null && value !== undefined && Number.isFinite(value);

  // Local raw string so the user can type freely (including transient
  // out-of-range values like "1" while typing "100"). We only parse +
  // clamp on blur or Enter — never per-keystroke, which previously made
  // the field "snap" mid-typing (min=50: typing "1" jumped to "50",
  // making "100" unreachable; same trap with max).
  //
  // The `prop` ref tracks the prop value the local string was synced
  // from, so an external prop change (server refetch, Reset button,
  // unrelated re-render) overrides the local string only when the
  // upstream value actually changed.
  const [raw, setRaw] = React.useState<string>(hasValue ? String(value) : "");
  const lastSyncedProp = React.useRef<number | null | undefined>(value);
  React.useEffect(() => {
    if (lastSyncedProp.current !== value) {
      lastSyncedProp.current = value;
      setRaw(hasValue ? String(value) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = () => {
    if (raw === "") {
      onChange(null);
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      onChange(null);
      setRaw("");
      return;
    }
    const clamped = clamp(parsed);
    onChange(clamped);
    if (clamped !== parsed) setRaw(String(clamped));
  };

  const envHint =
    placeholder && !hasValue
      ? "기본값은 env로 설정되어 있습니다. 입력 시 override 됩니다."
      : null;
  const rangeHint =
    field.min !== undefined || field.max !== undefined
      ? `Range: ${field.min ?? "-∞"} – ${field.max ?? "∞"}`
      : null;
  const unitHint = field.unit ? `Unit: ${field.unit}` : null;
  const helpParts = [envHint, rangeHint, unitHint, field.help].filter(
    Boolean,
  ) as React.ReactNode[];
  const help = helpParts.length
    ? helpParts.map((part, i) => (
        <span key={i} className="block">
          {part}
        </span>
      ))
    : undefined;
  return (
    <FormRow
      label={field.title ?? field.key}
      required={field.required}
      help={help}
    >
      <Input
        type="number"
        className="w-32"
        value={raw}
        placeholder={placeholder}
        min={field.min}
        max={field.max}
        disabled={disabled}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
      />
    </FormRow>
  );
}
